import { Button } from '#app/catalyst-ui/button.js'
import type { DialogControlProps } from '#app/catalyst-ui/dialog.js'
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '#app/catalyst-ui/dialog.js'
import { Description, FieldGroup, Label } from '#app/catalyst-ui/fieldset.js'
import { Subheading } from '#app/catalyst-ui/heading.js'
import { Link } from '#app/catalyst-ui/link.js'
import { Switch, SwitchField } from '#app/catalyst-ui/switch.js'
import { Text, TextLink } from '#app/catalyst-ui/text.js'
import type { Handle } from '#app/root.js'
import { getInputProps } from '#app/utils/conform.js'
import { setCookiePreferencesSessionData } from '#app/utils/cookie-preferences.server.js'
import { useHydrated } from '#app/utils/misc.js'
import { combineHeaders } from '#app/utils/misc.server.js'
import { useRequestInfo } from '#app/utils/request-info.js'
import { flashToast } from '#app/utils/toast.server.js'
import { getFormProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import * as Headless from '@headlessui/react'
import { CloseButton, Transition, useClose } from '@headlessui/react'
import type { ActionFunctionArgs, MetaFunction } from '@remix-run/node'
import { data } from '@remix-run/node'
import { Form, useActionData, useFetcher } from '@remix-run/react'
import { isEqual } from 'lodash-es'
import { useEffect, useState } from 'react'
import { z } from 'zod'

export const meta: MetaFunction = () => [
	{ title: 'Cookie Policy - GradeGuide' },
	{
		name: 'description',
		content: 'Learn about how GradeGuide uses cookies.',
	},
]

export const handle: Handle = {
	getSitemapEntries: () => [{ priority: 0.5, changeFreq: 'monthly' }],
}

const cookiesSchema = z.object({
	measureWebsiteUse: z.boolean().default(false),
	rememberSettings: z.boolean().default(false),
	marketingCookies: z.boolean().default(false),
})

export async function action({ request }: ActionFunctionArgs) {
	const submission = parseWithZod(await request.formData(), { schema: cookiesSchema })
	if (submission.status !== 'success' || !submission.value)
		return data({ submission: submission.reply() }, { status: 400 })

	const { headers: headers1 } = await setCookiePreferencesSessionData({ request, data: submission.value })

	const { headers: headers2 } = await flashToast({
		type: 'success',
		message: 'Cookie preferences saved',
	})

	return data({ submission: submission.reply() }, { headers: combineHeaders(headers1, headers2) })
}

export default function Index() {
	const cookiePreferences = useRequestInfo()?.cookiePrefs
	const actionData = useActionData<typeof action>()

	const [form, fields] = useForm({
		lastResult: actionData?.submission,
		onValidate: ({ formData }) => parseWithZod(formData, { schema: cookiesSchema }),
		constraint: getZodConstraint(cookiesSchema),
		defaultValue: cookiePreferences,
	})

	useEffect(() => {
		setMeasureWebsiteUse(cookiePreferences?.measureWebsiteUse ?? false)
		setRememberSettings(cookiePreferences?.rememberSettings ?? false)
		setMarketingCookies(cookiePreferences?.marketingCookies ?? false)
	}, [cookiePreferences])

	const [measureWebsiteUse, setMeasureWebsiteUse] = useState(cookiePreferences?.measureWebsiteUse ?? false)
	const [rememberSettings, setRememberSettings] = useState(cookiePreferences?.rememberSettings ?? false)
	const [marketingCookies, setMarketingCookies] = useState(cookiePreferences?.marketingCookies ?? false)

	const isChanged = !isEqual(cookiePreferences, { measureWebsiteUse, rememberSettings, marketingCookies })

	return (
		<>
			<h1>Cookie policy</h1>
			<p>
				<strong>Last updated:</strong> 16 April 2025
			</p>

			<p>
				This cookie policy explains how GradeGuide (www.gradeguide.co.uk) ("we", "our", or "us") uses cookies on our
				website. By using our website, you agree to the use of cookies as described in this policy.
			</p>

			<hr />

			<h2>What are cookies?</h2>
			<p>
				Cookies are small text files stored on your device when you visit a website. They help us recognise you, improve
				your experience, and enhance security.
			</p>

			<h2>Cookies we use</h2>
			<p>We use the following categories of cookies on our website:</p>

			<Form method="POST" {...getFormProps(form, { ariaAttributes: false })}>
				<ul className="!space-y-8">
					<li>
						<strong>Strictly necessary cookies</strong> – These cookies are essential for the operation of the website,
						enabling features such as authentication and verification.
						<div className="-mt-2">
							<p className="text-sm italic">These cookies cannot be disabled.</p>
						</div>
					</li>

					<li>
						<strong>Cookies that measure website use</strong> – These cookies help us understand how you interact with
						our website by collecting anonymised usage data.
						<Headless.Field className="mt-2 grid grid-cols-[min-content_auto] gap-4">
							<Switch
								{...getInputProps(fields.measureWebsiteUse, { type: 'checkbox' })}
								className="my-auto"
								checked={measureWebsiteUse}
								onChange={setMeasureWebsiteUse}
							/>
							<Label>Allow cookies that measure website use</Label>
						</Headless.Field>
					</li>

					<li>
						<strong>Cookies that remember your settings</strong> – These cookies store your preferences, such as
						language and display settings, to enhance your browsing experience.
						<Headless.Field className="mt-2 grid grid-cols-[min-content_auto] gap-4">
							<Switch
								{...getInputProps(fields.rememberSettings, { type: 'checkbox' })}
								className="my-auto"
								checked={rememberSettings}
								onChange={setRememberSettings}
							/>
							<Label>Allow cookies that remember your settings</Label>
						</Headless.Field>
					</li>

					<li>
						<strong>Cookies used for marketing, and advertising</strong> – These cookies assist in tailoring your
						content and advertisements based on user activity.
						<Headless.Field className="mt-2 grid grid-cols-[min-content_auto] gap-4">
							<Switch
								{...getInputProps(fields.marketingCookies, { type: 'checkbox' })}
								className="my-auto"
								checked={marketingCookies}
								onChange={setMarketingCookies}
							/>
							<Label>Allow cookies used for marketing, and advertising</Label>
						</Headless.Field>
					</li>
				</ul>

				<Button
					type="submit"
					disabled={!isChanged}
					className="mt-6"
					data-umami-event="cookies.preferences_button.clicked"
				>
					Save preferences
				</Button>
			</Form>

			<h2>Changes to this cookie policy</h2>
			<p>
				We may update this cookie policy from time to time. Any changes will be posted on this page with an updated
				"Last updated" date.
			</p>

			<h2>Contact us</h2>
			<p>
				If you have any questions about this cookie policy, please <Link href="/contact">contact us</Link>.
			</p>
		</>
	)
}

export function CookieBanner() {
	const cookiePreferences = useRequestInfo()?.cookiePrefs
	const fetcher = useFetcher<typeof action>()

	const hydrated = useHydrated()
	const [open, setOpen] = useState(false)

	return (
		<>
			<Transition show={!cookiePreferences && hydrated}>
				<section
					aria-labelledby="cookie_heading"
					className="fixed bottom-5 left-1/2 w-[calc(100%-theme(spacing.5)*2)] max-w-screen-lg -translate-x-1/2 transition duration-100 will-change-transform data-closed:translate-y-12 data-closed:opacity-0 data-enter:ease-out data-leave:ease-in sm:data-closed:translate-y-0 sm:data-closed:data-enter:scale-95"
					data-nosnippet
				>
					<div className="flex flex-col gap-6 rounded-lg bg-white px-5 py-4 shadow-lg ring-1 ring-zinc-950/10 sm:flex-row sm:gap-10 dark:bg-zinc-900 dark:ring-white/10">
						<div>
							<Subheading level={4} id="cookie_heading">
								Cookies on GradeGuide
							</Subheading>

							<Text className="mt-2">
								We use cookies to enhance your browsing experience, please choose which cookies you would like to enable
								or accept all. For more information on how we use cookies, please see our{' '}
								<Link href="/cookies" className="font-medium text-zinc-900 underline dark:text-white">
									Cookie policy
								</Link>
								.
							</Text>
						</div>

						<div className="flex flex-col items-center gap-3 sm:flex-row-reverse">
							<fetcher.Form method="POST" action="./cookies" className="w-full">
								{Object.keys(cookiesSchema.shape).map((key) => (
									<input key={key} type="hidden" name={key} value="on" />
								))}

								<Button
									className="w-full whitespace-nowrap"
									type="submit"
									disabled={fetcher.state !== 'idle' || !!cookiePreferences}
									data-umami-event="cookie_banner.accept_all_button.clicked"
								>
									Accept all
								</Button>
							</fetcher.Form>

							<Button
								className="w-full whitespace-nowrap"
								onClick={() => setOpen(true)}
								data-umami-event="cookie_banner.choose_button.clicked"
							>
								Let me choose
							</Button>
						</div>
					</div>
				</section>
			</Transition>

			<CookiesDialog open={open} onClose={setOpen} />
		</>
	)
}

function CookiesDialog({ ...props }: DialogControlProps) {
	return (
		<Dialog size="md" {...props}>
			<DialogContent />
		</Dialog>
	)
}

function DialogContent() {
	const cookiePreferences = useRequestInfo()?.cookiePrefs
	const onClose = useClose()
	const fetcher = useFetcher<typeof action>()

	const [form, fields] = useForm({
		lastResult: fetcher.data?.submission,
		onValidate: ({ formData }) => parseWithZod(formData, { schema: cookiesSchema }),
		constraint: getZodConstraint(cookiesSchema),
		defaultValue:
			cookiePreferences ??
			({
				measureWebsiteUse: false,
				rememberSettings: false,
				marketingCookies: false,
			} satisfies z.infer<typeof cookiesSchema>),
	})

	useEffect(() => {
		if (fetcher.data?.submission.status === 'success') onClose()
	}, [fetcher.data?.submission.status, onClose])

	return (
		<fetcher.Form method="POST" action="./cookies" {...getFormProps(form, { ariaAttributes: false })}>
			<DialogTitle>Manage cookies</DialogTitle>
			<DialogDescription className="">
				We use cookies to enhance your browsing experience, please choose which cookies you would like to enable. For
				more information on how we use cookies, please see our{' '}
				<TextLink href="/cookies" onClick={onClose}>
					Cookie policy
				</TextLink>
				.
			</DialogDescription>

			<DialogBody>
				<FieldGroup>
					<SwitchField>
						<Label>Cookies that measure website use</Label>
						<Description>
							These cookies help us understand how you interact with our website by collecting anonymised usage data.
						</Description>
						<Switch {...getInputProps(fields.measureWebsiteUse, { type: 'checkbox' })} />
					</SwitchField>

					<SwitchField>
						<Label>Cookies that remember your settings</Label>
						<Description>
							These cookies store your preferences, such as language and display settings, to enhance your browsing
							experience.
						</Description>
						<Switch {...getInputProps(fields.rememberSettings, { type: 'checkbox' })} />
					</SwitchField>

					<SwitchField>
						<Label>Cookies used for marketing, and advertising</Label>
						<Description>
							These cookies assist in tailoring your content and advertisements based on user activity.
						</Description>
						<Switch {...getInputProps(fields.marketingCookies, { type: 'checkbox' })} />
					</SwitchField>
				</FieldGroup>
			</DialogBody>

			<DialogActions>
				<CloseButton as={Button} plain data-umami-event="cookies_dialog.cancel_button.clicked">
					Cancel
				</CloseButton>

				<Button type="submit" disabled={fetcher.state !== 'idle'} data-umami-event="cookies_dialog.save_button.clicked">
					Save preferences
				</Button>
			</DialogActions>
		</fetcher.Form>
	)
}
